import { CAREPLAN_ADL_PBS_ID } from '@lmc/common/types'
import type { AdlSectionExtended } from '@lmc/ts-common'
import dayjs from 'dayjs'
import { capitalize, some } from 'lodash'
import type React from 'react'
import { Component } from 'react'
import { getNominative } from '../../common/utils'
import { hasMeaningfulStoredHtml } from '../../common/utils/html'
import {
  eolSectionConfig,
  PlaceOfCareSnomedDescriptionEnum
} from '../../features/carePlans/components/endOfLife'
import PdfExport from './PdfExport'
import {
  Header,
  PageNumbers,
  RiskAssessmentActionsTable,
  RiskAssessmentsTable,
  TitleTextPair
} from './pdfContent'
import { type PdfmakeContent, richHtmlToContent } from './richTextPdfContent'
import { tableStyles } from './styles'

interface CarePlanPdfExportProps {
  carePlan: any
  carer: any
  resident: any
  carePlanNominative?: string
  logs: any[]
  disabled?: boolean
  style: React.CSSProperties
  title: string
}

const formatDate = (date: any) => dayjs(date).format('DD-MM-YYYY')

const formatTime = (time: any) => dayjs(time).format('HH:mm')

const getName = (item: any) => `${item.name.first} ${item.name.last}`

const formatStructuredSectionText = (section: any): string | null => {
  const config = eolSectionConfig.find(
    (c) => c.sectionType === section.sectionType
  )
  const entryLines = section.entries
    ?.map((entry: any) => {
      if (!config) {
        return Object.values(entry)
          .filter((v): v is string => typeof v === 'string' && v.length > 0)
          .join(' | ')
      }
      return config.fields
        .map((field) => {
          const value = entry[field.key]
          if (!value) {
            return null
          }
          if (field.type === 'date') {
            return `${field.label}: ${formatDate(value)}`
          }
          if (field.type === 'select') {
            const label =
              PlaceOfCareSnomedDescriptionEnum[
                value as keyof typeof PlaceOfCareSnomedDescriptionEnum
              ] ?? value
            return `${field.label}: ${label}`
          }
          return `${field.label}: ${value}`
        })
        .filter(Boolean)
        .join('\n')
    })
    ?.filter((t: string) => t.length > 0)
    ?.join('\n\n')
  return entryLines || null
}

interface PdfDocSection {
  title: string
  titleStyle: string
  text?: string | number
  textStyle?: string
  content?: PdfmakeContent
}

class CarePlanPdfExport extends Component<CarePlanPdfExportProps> {
  getCarePlanDoc = () => {
    const {
      carePlan: {
        customName,
        carePlanAdl,
        createdOn,
        editedOn,
        nextReviewDate,
        riskAssessments,
        reviewComment
      },
      logs,
      carer,
      resident,
      carePlanNominative = 'Care Plans'
    } = this.props

    const PBSSections = this.props.carePlan?.sections

    const isPBSAssessment =
      PBSSections?.length &&
      this.props.carePlan.carePlanAdl.id === CAREPLAN_ADL_PBS_ID

    const reviewedOnText = editedOn
      ? `${formatDate(editedOn)} at ${formatTime(editedOn)} - ${getName(carer)}`
      : `${formatDate(createdOn)} at ${formatTime(createdOn)}`
    const nextReviewText = nextReviewDate ? formatDate(nextReviewDate) : 'N/A'

    let sections: PdfDocSection[] = [
      {
        title: 'Reviewed on:',
        titleStyle: 'tableHeader',
        text: reviewedOnText,
        textStyle: 'tableHeaderSmall'
      },
      {
        title: 'Next review:',
        titleStyle: 'tableHeader',
        text: nextReviewText,
        textStyle: 'tableHeaderSmall'
      }
    ]

    if (reviewComment) {
      sections.splice(1, 0, {
        title: 'Comment:',
        titleStyle: 'tableHeader',
        text: reviewComment,
        textStyle: 'tableHeaderSmall'
      })
    }

    if (isPBSAssessment) {
      PBSSections?.forEach((section: AdlSectionExtended) => {
        sections = [
          ...sections,
          {
            title: capitalize(section?.label),
            titleStyle: 'h2',
            text: section?.value || '',
            textStyle: 'tableHeaderSmall'
          }
        ]
      })
    }

    // strengths/needs carry rich-text HTML; render it with full formatting so the
    // PDF matches the on-screen view. The stored plain field has had its newlines
    // flattened to spaces on write, so it cannot be used. outcomes has no HTML
    // counterpart and falls back to its plain value (pdfmake honours any \n).
    const richHtmlByField: Record<string, string | undefined> = {
      strengths: this.props.carePlan.strengthsHtml,
      needs: this.props.carePlan.needsHtml
    }
    // eslint-disable-next-line no-restricted-syntax, prefer-const
    for (const section of ['outcomes', 'strengths', 'needs']) {
      const html = richHtmlByField[section]
      if (html && hasMeaningfulStoredHtml(html)) {
        sections = [
          ...sections,
          {
            title: capitalize(section),
            titleStyle: 'h2',
            content: richHtmlToContent(html)
          }
        ]
      } else {
        const text = this.props.carePlan[section]
        if (text) {
          sections = [
            ...sections,
            {
              title: capitalize(section),
              titleStyle: 'h2',
              text,
              textStyle: 'tableHeaderSmall'
            }
          ]
        }
      }
    }

    if (!isPBSAssessment) {
      PBSSections?.filter((s: any) => s.sectionType).forEach((section: any) => {
        const entryText = formatStructuredSectionText(section)
        if (entryText) {
          sections = [
            ...sections,
            {
              title: section.label,
              titleStyle: 'h2',
              text: entryText,
              textStyle: 'tableHeaderSmall'
            }
          ]
        }
      })
    }

    // Rich sections carry pre-built pdfmake content (a title block + the converted
    // HTML); plain sections keep using the shared title/text pair helper.
    const renderDocSection = (s: PdfDocSection) => {
      if (s.content !== undefined) {
        return [
          { style: s.titleStyle, text: s.title },
          {
            margin: [0, 0, 0, 30],
            stack: Array.isArray(s.content) ? s.content : [s.content]
          }
        ]
      }
      return TitleTextPair({
        title: s.title,
        titleStyle: s.titleStyle,
        text: s.text ?? '',
        textStyle: s.textStyle ?? 'tableHeaderSmall'
      })
    }

    let content = [
      Header({
        title: `${getName(resident)} - ${getNominative(
          carePlanNominative,
          'Care Plan'
        )}`,
        subtitle: customName || carePlanAdl.name
      } as any),
      ...sections.map(renderDocSection)
    ]

    if (riskAssessments?.length) {
      content = [
        // @ts-ignore
        ...content,
        [
          {
            style: 'h2',
            text: 'Risk Assessments'
          },
          // @ts-ignore
          RiskAssessmentsTable(riskAssessments)
        ]
      ]
      if (some(riskAssessments, this.hasActions)) {
        content = [
          // @ts-ignore
          ...content,
          [
            {
              style: 'h2',
              text: 'Actions'
            },
            // @ts-ignore
            RiskAssessmentActionsTable(riskAssessments.filter(this.hasActions))
          ]
        ]
      }
    }

    if (logs?.length) {
      content = [
        // @ts-ignore
        ...content,
        [
          {
            style: 'h2',
            text: 'Signatures'
          },
          // @ts-ignore
          ...logs.map((log) => ({
            text: `${formatDate(log.timeLogged)} - ${log.description}`,
            textStyle: 'tableHeaderSmall'
          }))
        ]
      ]
    }

    return {
      content,
      footer: PageNumbers,
      styles: tableStyles
    }
  }

  getDocDefinition = () => {
    const { carePlan, resident } = this.props
    return new Promise((resolve, reject) => {
      if (!carePlan || !resident) {
        // eslint-disable-next-line prefer-promise-reject-errors
        reject('Data not provided')
      } else {
        resolve(this.getCarePlanDoc())
      }
    })
  }

  hasActions = (assessment: any) => assessment.formData?.actions

  render() {
    const { disabled, style, title } = this.props
    return (
      <PdfExport
        getDocDefinition={this.getDocDefinition}
        pdfTitle={title}
        style={style}
        // @ts-ignore
        disabled={disabled}
      />
    )
  }
}

export default CarePlanPdfExport
